/**
 * SceneVisualizers - Debug/diagnostic visualization of slice planes,
 * plane-plane intersections and slots in the 3D scene
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * The tracking arrays (planeVisualizations, intersectionLines,
 * generatedPlanes, slotVisualizations) stay on the Application.
 */
import * as THREE from 'three';

export class SceneVisualizers {
    constructor(app) {
        this.app = app;
    }

    /**
     * Visualize a single slice plane
     * @param {SlicePlane} slicePlane - The slice plane to visualize
     * @param {number} planeIndex - Index of plane in sequence
     * @param {number} splineIndex - Index of parent spline
     */
    visualizePlane(slicePlane, planeIndex, splineIndex) {
        // Create a plane mesh for visualization
        const planeGeometry = new THREE.PlaneGeometry(5, 5);

        // Alternate colors for visibility
        const colors = [0x4CAF50, 0x2196F3, 0xFF9800, 0x9C27B0];
        const color = colors[splineIndex % colors.length];

        const planeMaterial = new THREE.MeshBasicMaterial({
            color: color,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.3,
            wireframe: false
        });
        const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

        // Apply the slice plane's transform
        slicePlane.applyTransform(planeMesh);

        this.app.sceneManager.add(planeMesh);

        // Add wireframe edges
        const edges = new THREE.EdgesGeometry(planeGeometry);
        const lineMaterial = new THREE.LineBasicMaterial({
            color: color,
            linewidth: 2
        });
        const wireframe = new THREE.LineSegments(edges, lineMaterial);
        slicePlane.applyTransform(wireframe);

        this.app.sceneManager.add(wireframe);

        // Store for cleanup
        this.app.generatedPlanes.push({
            slicePlane: slicePlane,
            mesh: planeMesh,
            wireframe: wireframe,
            planeIndex: planeIndex,
            splineIndex: splineIndex
        });
    }

    /**
     * Visualize plane intersections
     * Creates line visualizations where planes intersect
     */
    visualizeIntersections() {
        console.log('Visualizing intersections...');

        // Clear existing intersection visualizations
        this.app.intersectionLines.forEach(line => {
            if (line.mesh) {
                this.app.sceneManager.remove(line.mesh);
                line.mesh.geometry.dispose();
                line.mesh.material.dispose();
            }
        });
        this.app.intersectionLines = [];

        // Get all intersections from manager
        const intersections = this.app.slicePlaneManager.intersections;

        console.log('Found', intersections.size, 'intersections to visualize');

        // Visualize each intersection
        for (const [key, intersection] of intersections) {
            this.visualizeIntersection(intersection);
        }
    }

    /**
     * Visualize a single plane intersection
     * @param {Object} intersection - Intersection data from SlicePlaneManager
     */
    visualizeIntersection(intersection) {
        const { plane1, plane2, line, pointsOnPlane1, pointsOnPlane2 } = intersection;

        // Create line geometry for the intersection
        const points = [];

        // Use the intersection line, but limit it to a reasonable length
        const start = line.start.clone();
        const end = line.end.clone();

        // Scale down to match our visualization scale
        start.multiplyScalar(0.001);
        end.multiplyScalar(0.001);

        points.push(start);
        points.push(end);

        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
            color: 0xFF0000, // Red for intersections
            linewidth: 3
        });

        const lineMesh = new THREE.Line(geometry, material);
        this.app.sceneManager.add(lineMesh);

        // Store for cleanup
        this.app.intersectionLines.push({
            mesh: lineMesh,
            plane1: plane1,
            plane2: plane2
        });

        console.log(`Visualized intersection between plane ${plane1.id} and ${plane2.id}`);
    }

    /**
     * Visualize all slots as 3D geometry
     * Shows where slots will be cut into each plane
     */
    visualizeSlots() {
        console.log('Visualizing slots...');

        // Clear existing slot visualizations
        this.app.slotVisualizations.forEach(slotViz => {
            if (slotViz.mesh) {
                this.app.sceneManager.remove(slotViz.mesh);
                slotViz.mesh.geometry.dispose();
                slotViz.mesh.material.dispose();
            }
        });
        this.app.slotVisualizations = [];

        // Get all slots from manager
        const slots = this.app.slicePlaneManager.getSlots();

        console.log('Found', slots.length, 'slots to visualize');

        // Visualize each slot
        slots.forEach((slot, index) => {
            this.visualizeSlot(slot, index);
        });
    }

    /**
     * Visualize a single slot as 3D geometry
     * @param {SliceSlot} slot - Slot to visualize
     * @param {number} index - Slot index
     */
    visualizeSlot(slot, index) {
        if (!slot.plane) return;

        // Get all outlines for this slot (handles finger joints)
        const outlines = slot.getAllOutlines();

        outlines.forEach((outline, outlineIndex) => {
            // Get the 4 corners in 2D
            if (outline.length !== 4) return;

            // Convert corners to 3D world space using the plane's PROFILE frame
            // (matches where the slot is actually cut into the mesh)
            const corners3D = outline.map(corner => slot.plane.getProfileWorldPos(corner));

            // Calculate center and dimensions
            const center = new THREE.Vector3();
            corners3D.forEach(c => center.add(c));
            center.divideScalar(corners3D.length);

            // Calculate width and height
            const width = corners3D[0].distanceTo(corners3D[1]);
            const height = corners3D[1].distanceTo(corners3D[2]);

            // Create box geometry for the slot
            const geometry = new THREE.BoxGeometry(width, height, slot.plane.thickness * 0.01);

            // Yellow/orange material for slots (different from planes and intersections)
            const material = new THREE.MeshBasicMaterial({
                color: 0xFFAA00,
                transparent: true,
                opacity: 0.5,
                wireframe: false
            });

            const mesh = new THREE.Mesh(geometry, material);

            // Position and orient the mesh
            mesh.position.copy(center);

            // Orient the box from the slot rectangle's own edge directions
            const xDir = new THREE.Vector3().subVectors(corners3D[1], corners3D[0]).normalize();
            const yDir = new THREE.Vector3().subVectors(corners3D[2], corners3D[1]).normalize();
            const zDir = new THREE.Vector3().crossVectors(xDir, yDir).normalize();
            const basis = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
            mesh.quaternion.setFromRotationMatrix(basis);

            this.app.sceneManager.add(mesh);

            // Store for cleanup
            this.app.slotVisualizations.push({
                slot: slot,
                mesh: mesh,
                index: index,
                outlineIndex: outlineIndex
            });
        });
    }

    /**
     * Visualize slice planes in the 3D scene
     * Shows semi-transparent planes and their intersection lines
     */
    visualizeSlicePlanes() {
        // Clear existing visualizations
        this.app.planeVisualizations.forEach(mesh => {
            this.app.sceneManager.remove(mesh);
            mesh.geometry.dispose();
            mesh.material.dispose();
        });
        this.app.planeVisualizations = [];

        this.app.intersectionLines.forEach(line => {
            this.app.sceneManager.remove(line);
            line.geometry.dispose();
            line.material.dispose();
        });
        this.app.intersectionLines = [];

        // Visualize each plane
        this.app.slicePlaneManager.getPlanes().forEach((slicePlane, index) => {
            // Create a large plane mesh for visualization
            const planeGeometry = new THREE.PlaneGeometry(10, 10);
            const planeMaterial = new THREE.MeshBasicMaterial({
                color: index === 0 ? 0xff6b6b : 0x4ecdc4,
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.3,
                wireframe: false
            });
            const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

            // Apply the slice plane's transform
            slicePlane.applyTransform(planeMesh);

            this.app.sceneManager.add(planeMesh);
            this.app.planeVisualizations.push(planeMesh);

            // Add wireframe edges
            const edges = new THREE.EdgesGeometry(planeGeometry);
            const lineMaterial = new THREE.LineBasicMaterial({
                color: index === 0 ? 0xff0000 : 0x00ffff,
                linewidth: 2
            });
            const wireframe = new THREE.LineSegments(edges, lineMaterial);
            slicePlane.applyTransform(wireframe);

            this.app.sceneManager.add(wireframe);
            this.app.planeVisualizations.push(wireframe);
        });

        // Visualize intersections
        this.app.slicePlaneManager.intersections.forEach((intersection, key) => {
            const line = intersection.line;

            // Create a thick line to visualize the intersection
            const points = [line.start, line.end];
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({
                color: 0xffff00,
                linewidth: 4
            });
            const lineMesh = new THREE.Line(geometry, material);

            this.app.sceneManager.add(lineMesh);
            this.app.intersectionLines.push(lineMesh);

            // Add spheres at intersection points
            intersection.pointsOnPlane1.forEach(point => {
                const sphereGeometry = new THREE.SphereGeometry(0.1, 16, 16);
                const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
                const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
                sphere.position.copy(point.worldPos);

                this.app.sceneManager.add(sphere);
                this.app.intersectionLines.push(sphere);
            });
        });

        console.log('Visualized', this.app.slicePlaneManager.getPlanes().length, 'planes and',
            this.app.slicePlaneManager.intersections.size, 'intersections');
    }
}
